import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Select,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import {
  RESIDENCY_MODES,
  RESIDENCY_MODE_EFFECT,
  type ResidencyMode,
  type ResidencyPolicyRow,
} from '@dayon/contracts';

import { api, ApiRequestError } from '../../../lib/api.ts';

/**
 * Set the residency policy for one scope.
 *
 * ONE FORM FOR TWO ROWS, and that is the reason it exists rather than being an
 * Edit on each parameter. `player.residency_mode` and
 * `player.residency_countries` are two rows in `platform.setting` and one
 * decision: a brand moved from ACCEPT_ALL to BLOCK_LIST one parameter at a time
 * spends the interval between the two saves at BLOCK_LIST with an empty list,
 * which reads as enforcement on every screen and refuses nobody.
 * `POST /api/residency/policy` writes both in one transaction and the database
 * checks the PAIR at commit, so that interval does not exist.
 *
 * THREE THINGS THIS DIALOG SAYS THAT ARE TRUE OF THE ROUTE:
 *
 *   · IT TAKES EFFECT IMMEDIATELY, on the next registration. There is no
 *     staging and no second approver — configuration does not go through the
 *     maker-checker the money paths use.
 *   · IT DOES NOT TOUCH EXISTING ACCOUNTS. `residency_permitted` is asked at
 *     REGISTRATION. Nothing sweeps the roster, nothing closes an account, and
 *     nothing anywhere in this platform re-checks a player who is already in.
 *     Saying otherwise would be the most consequential false claim on the
 *     screen.
 *   · IT IS LOGGED, twice, with the actor — one configuration-log row per
 *     parameter, in the same transaction as the values.
 */

export function ResidencyPolicyDialog({
  target,
  countries,
  onClose,
  onDone,
}: {
  /** The scope being edited: a brand row, or the platform-wide row. Null closed. */
  target: ResidencyPolicyRow | null;
  /** Every country the route will accept, from the same read as the policy. */
  countries: { code: string; name: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [mode, setMode] = useState<ResidencyMode>('ACCEPT_ALL');
  const [chosen, setChosen] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /* Seeded from the EFFECTIVE policy, including the half a brand inherits. A
     form pre-filled with only the brand's own rows would silently propose
     ACCEPT_ALL for a brand that is currently following a platform-wide block
     list — a downgrade nobody asked for, presented as the current state. */
  useEffect(() => {
    setMode(target?.mode ?? 'ACCEPT_ALL');
    setChosen(target?.countries ?? []);
    setFields({});
  }, [target]);

  const close = () => {
    setFields({});
    onClose();
  };

  const scopeLabel = target?.brandName ?? 'every brand';
  const nameOf = (code: string) => countries.find((c) => c.code === code)?.name ?? code;

  const add = (code: string) => {
    if (!code || chosen.includes(code)) return;
    setChosen((list) => [...list, code].toSorted((a, b) => nameOf(a).localeCompare(nameOf(b), 'en')));
    setFields({});
  };

  const submit = async () => {
    if (!target) return;
    /* Inline, because it is field validation and the operator can fix it
       without leaving the form. The route and the database both refuse it too —
       this is the first of three, not the only one. */
    if (mode !== 'ACCEPT_ALL' && chosen.length === 0) {
      setFields({
        countries:
          mode === 'ALLOW_LIST'
            ? 'An allow list naming no country admits nobody: every registration on this scope would be refused. Name at least one country, or choose “Accept every country”.'
            : 'A block list naming no country refuses nobody, while this screen would report a restriction. Name at least one country, or choose “Accept every country”.',
      });
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<{
        policy: ResidencyPolicyRow;
        modeChanged: boolean;
        countriesChanged: boolean;
        overrideCreated: boolean;
      }>('/api/residency/policy', {
        ...(target.brandId !== null ? { brandId: target.brandId } : {}),
        mode,
        countries: chosen,
      });

      const where = result.policy.brandName ?? 'every brand';
      toast(
        result.policy.mode === 'ACCEPT_ALL'
          ? `${where}: no residency restriction is in force. Logged against you.`
          : `${where}: ${RESIDENCY_MODE_EFFECT[result.policy.mode].label.toLowerCase()} — ${result.policy.countries.join(', ')}. Logged against you.`,
      );
      onDone();
      close();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not change the residency policy',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed, and nothing was logged. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const effect = RESIDENCY_MODE_EFFECT[mode];

  return (
    <Dialog
      open={target !== null}
      onClose={close}
      title={target ? `Residency policy — ${scopeLabel}` : 'Residency policy'}
      actions={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            Save the policy
          </Button>
        </>
      }
    >
      <div className="stack">
        {target && target.brandId === null ? (
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              This is the <strong>platform-wide</strong> policy. Every brand that has no policy of
              its own follows it — today that is{' '}
              <span className="tabular">every brand without an override</span>. To change one
              brand, close this and edit that brand&rsquo;s row instead.
            </div>
          </div>
        ) : null}

        {target && target.brandId !== null && (target.modeInherited || target.countriesInherited) ? (
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {target.brandName} has no policy of its own and is currently following the
              platform-wide one. Saving here gives it an <strong>override</strong>: it stops
              following the platform-wide value, including any later change to it.
            </div>
          </div>
        ) : null}

        <Field label="Mode" hint={effect.effect}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as ResidencyMode);
                setFields({});
              }}
            >
              {RESIDENCY_MODES.map((m) => (
                <option key={m} value={m}>
                  {RESIDENCY_MODE_EFFECT[m].label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {!effect.enforces ? (
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              While the mode is <strong>Accept every country</strong> the platform enforces{' '}
              <strong>no residency restriction at all</strong>. A resident of any country may open
              an account on this scope. An offshore PAGCOR licence would require blocking residents
              of the Philippines; a domestic PIGO licence would require admitting only them. This
              console does not know which licence the operator holds — nothing in the platform
              records it — so it cannot tell you which of those is the right setting.
            </div>
          </div>
        ) : null}

        <Field
          label="Countries"
          error={fields['countries']}
          hint={
            effect.enforces
              ? 'ISO 3166-1 alpha-2, compared against the country each player declared at registration. The same list the picker on the sign-up form offers.'
              : 'Kept but not read: under “Accept every country” the platform never consults this list. It is saved so a prepared list survives switching the mode back on.'
          }
        >
          {({ id, describedBy, invalid }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value=""
              onChange={(e) => add(e.target.value)}
            >
              <option value="">Add a country…</option>
              {countries
                .filter((c) => !chosen.includes(c.code))
                .map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
            </Select>
          )}
        </Field>

        {chosen.length === 0 ? (
          <div className="text-subtle" style={{ fontSize: '12.5px' }}>
            No country is on the list.
          </div>
        ) : (
          <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {chosen.map((code) => (
              <Tag key={code} tone="outline">
                {nameOf(code)} <span className="tabular">({code})</span>{' '}
                <Button
                  small
                  variant="ghost"
                  aria-label={`Remove ${nameOf(code)} from the list`}
                  onClick={() => setChosen((list) => list.filter((c) => c !== code))}
                >
                  <Icon name="x" />
                </Button>
              </Tag>
            ))}
          </div>
        )}

        <div className="notice">
          <Icon name="warning-circle" />
          <div>
            This applies to <strong>the next registration</strong> and to nothing else. Players
            already on the roster are <strong>not</strong> re-checked, not closed and not flagged:
            the rule is asked once, at sign-up, and no job anywhere in this platform revisits it.
            If a change here would exclude accounts that already exist, the residence breakdown on
            this screen is how you find them — closing them is a separate decision and a separate
            action. The change itself takes effect immediately, with no staging and no second
            approver, and is recorded in the configuration log with your name, one row per
            parameter, in the same transaction as the values.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
